import { EnvironmentManager } from '../services/EnvironmentManager/EnvironmentManager';

// ─── Method badge colors ──────────────────────────────────────────────────────
const METHOD_COLORS: Record<string, string> = {
  GET:    '#2196F3',
  POST:   '#4CAF50',
  PUT:    '#FF9800',
  PATCH:  '#9C27B0',
  DELETE: '#F44336',
};

// Strip the domain so the log stays readable in the console
const stripDomain = (url: string) => url.replace(/https?:\/\/[^/]+/, '');

// ─── API Logger ───────────────────────────────────────────────────────────────
/**
 * Logs outgoing API calls and their responses.
 * Silent in production.
 */
export class APILogger {

  /** Log a successful API call */
  static log(method: string, url: string, body: any, response: any): void {
    if (EnvironmentManager.isProdEnv()) return;

    const color    = METHOD_COLORS[method.toUpperCase()] ?? '#607D8B';
    const endpoint = stripDomain(url);

    console.groupCollapsed(
      `%c ${method.toUpperCase()} %c ${endpoint}`,
      `background:${color};color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px;font-size:11px`,
      'color:#888;font-size:12px',
    );
    if (body !== undefined && body !== null) {
      console.log('%c ⬆  Request Body', 'color:#FF9800;font-weight:bold', body);
    }
    console.log('%c ⬇  Response', 'color:#4CAF50;font-weight:bold', response);
    console.groupEnd();
  }

  /** Log a failed API call */
  static error(method: string, url: string, body: any, error: any): void {
    if (EnvironmentManager.isProdEnv()) return;

    const endpoint = stripDomain(url);

    console.group(
      `%c ✗ ${method.toUpperCase()} FAILED %c ${endpoint}`,
      'background:#F44336;color:#fff;font-weight:bold;padding:2px 8px;border-radius:4px;font-size:11px',
      'color:#F44336;font-size:12px',
    );
    if (body !== undefined && body !== null) {
      console.log('%c ⬆  Request Body', 'color:#FF9800;font-weight:bold', body);
    }
    console.error('%c Error', 'color:#F44336;font-weight:bold', error);
    console.groupEnd();
  }
}

// ─── Error Logger ─────────────────────────────────────────────────────────────
/**
 * Logs errors from catch blocks with the call location from the stack trace.
 * Silent in production.
 *
 * Usage:   ErrorLogger.log(error, 'MyClassName')
 */
export class ErrorLogger {

  static log(error: any, context?: string): void {
    if (EnvironmentManager.isProdEnv()) return;

    // Stack: [0] Error header, [1] ErrorLogger.log, [2] the actual caller
    const stackLines = (new Error().stack ?? '').split('\n');
    const callerLine = stackLines[2]?.trim() ?? '';

    const label = context ? ` [${context}]` : '';

    console.group(`%c ✗ Error${label}`, 'color:#F44336;font-weight:bold;font-size:12px');
    console.error(
      '%c Message',
      'color:#FF7043;font-weight:bold',
      error instanceof Error ? error.message : error,
    );
    if (callerLine) {
      console.log('%c Location', 'color:#78909C;font-weight:bold', callerLine);
    }
    if (error instanceof Error && error.stack) {
      console.log('%c Stack', 'color:#B0BEC5', error.stack);
    }
    console.groupEnd();
  }
}
